import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

export function PaymentHistoryLegend() {
  const [showLegend, setShowLegend] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, right: 0 });
  const legendRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (legendRef.current && !legendRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setShowLegend(false);
      }
    };

    if (showLegend) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLegend]);

  useEffect(() => {
    if (showLegend && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [showLegend]);

  return (
    <>
      <style>{`
        .legend-button-wrapper {
          position: relative;
        }
        
        .legend-button {
          padding: 6px 20px;
          border: 1px solid #D1D5DB;
          border-radius: 20px;
          background: white;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .legend-button:hover {
          background: #F3F4F6;
          border-color: #9CA3AF;
        }
        
        .legend-popup {
          position: fixed;
          background: white;
          border: 1px solid #D1D5DB;
          border-radius: 8px;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          padding: 20px;
          z-index: 9999;
          width: 520px;
          max-width: 90vw;
        }
        
        .legend-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .legend-title {
          font-size: 18px;
          font-weight: 700;
          color: #1F2937;
        }
        
        .legend-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          color: #6B7280;
          transition: color 0.2s;
        }
        
        .legend-close:hover {
          color: #1F2937;
        }
        
        .legend-grid {
          display: grid;
          grid-template-columns: 1fr 1.3fr;
          row-gap: 8px;
          column-gap: 24px;
        }
        
        .legend-item {
          display: flex;
          align-items: center;
          gap: 12px;
          min-height: 28px;
        }
        
        .legend-badge {
          padding: 4px 10px;
          border-radius: 3px;
          font-weight: 600;
          font-size: 12px;
          min-width: 40px;
          text-align: center;
          color: white;
        }
        
        .legend-badge.ok {
          background: #4ADE80;
        }
        
        .legend-badge.no-data-gray {
          background: #D1D5DB;
          color: #6B7280;
        }
        
        .legend-badge.no-data-white {
          background: white;
          color: #6B7280;
          border: 1px solid #D1D5DB;
        }
        
        .legend-badge.late-30 {
          background: #F59E0B;
        }
        
        .legend-badge.late-60 {
          background: #F97316;
        }
        
        .legend-badge.late-90 {
          background: #EF4444;
        }
        
        .legend-badge.late-120 {
          background: #DC2626;
        }
        
        .legend-badge.late-150 {
          background: #B91C1C;
        }
        
        .legend-badge.late-180 {
          background: #991B1B;
        }
        
        .legend-badge.code {
          background: #374151;
        }
        
        .legend-text {
          font-size: 14px;
          color: #374151;
        }
        
        .legend-note {
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid #E5E7EB;
          font-size: 13px;
          color: #6B7280;
          line-height: 1.5;
        }
      `}</style>
      <div className="legend-button-wrapper">
        <button 
          ref={buttonRef}
          className="legend-button"
          onClick={() => setShowLegend(!showLegend)}
        >
          Legend
        </button>
        {showLegend && (
          <div 
            ref={legendRef} 
            className="legend-popup"
            style={{
              top: `${popupPosition.top}px`,
              right: `${popupPosition.right}px`
            }}
          >
            <div className="legend-header">
              <div className="legend-title">Legend</div>
              <button 
                className="legend-close"
                onClick={() => setShowLegend(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="legend-grid">
              <div className="legend-item">
                <div className="legend-badge ok">OK</div>
                <div className="legend-text">Current</div>
              </div>
              <div className="legend-item">
                <div className="legend-badge late-150">150</div>
                <div className="legend-text">150 Days Late</div>
              </div>
              <div className="legend-item">
                <div className="legend-badge no-data-gray">&nbsp;</div>
                <div className="legend-text">No Data Provided*</div>
              </div>
              <div className="legend-item">
                <div className="legend-badge late-180">180</div>
                <div className="legend-text">180 Days Late</div>
              </div>
              <div className="legend-item">
                <div className="legend-badge no-data-white">&nbsp;</div>
                <div className="legend-text">No Data Provided</div>
              </div>
              <div className="legend-item">
                <div className="legend-badge code">CO</div>
                <div className="legend-text">Chargeoff or Collection</div>
              </div>
              <div className="legend-item">
                <div className="legend-badge late-30">30</div>
                <div className="legend-text">30 Days Late</div>
              </div>
              <div className="legend-item">
                <div className="legend-badge code">RF</div>
                <div className="legend-text">Repossession or Foreclosure</div>
              </div>
              <div className="legend-item">
                <div className="legend-badge late-60">60</div>
                <div className="legend-text">60 Days Late</div>
              </div>
              <div className="legend-item">
                <div className="legend-badge code">PP</div>
                <div className="legend-text">Payment Plan</div>
              </div>
              <div className="legend-item">
                <div className="legend-badge late-90">90</div>
                <div className="legend-text">90 Days Late</div>
              </div>
              <div className="legend-item">
                <div className="legend-badge code">VS</div>
                <div className="legend-text">Voluntary Surrender</div>
              </div>
              <div className="legend-item">
                <div className="legend-badge late-120">120</div>
                <div className="legend-text">120 Days Late</div>
              </div>
            </div>
            <div className="legend-note">
              *Sometimes the credit bureaus do not have information from a particular month on file.
            </div>
          </div>
        )}
      </div>
    </>
  );
}

